import React, { useState, useEffect } from 'react';
import axios from 'axios';

function EditAddress({ onClose, userId, addressId, fetchAddresses }) {
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        pincode: '',
        locality: '',
        // 🔹 Split address into separate fields
        houseNo: '',
        buildingName: '',
        street: '',
        landmark: '',
        area: '',
        // Keep existing fields
        city: '',
        district: '',
        state: '',
        addressType: 'Home',
        ledcode: userId,
    });

    const STATE_MAP = {
        AN: "Andaman and Nicobar Islands",
        AP: "Andhra Pradesh",
        AR: "Arunachal Pradesh",
        AS: "Assam",
        BR: "Bihar",
        CG: "Chandigarh",
        CH: "Chhattisgarh",
        DN: "Dadra and Nagar Haveli",
        DD: "Daman and Diu",
        DL: "Delhi",
        GA: "Goa",
        GJ: "Gujarat",
        HR: "Haryana",
        HP: "Himachal Pradesh",
        JK: "Jammu and Kashmir",
        JH: "Jharkhand",
        KA: "Karnataka",
        KL: "Kerala",
        LA: "Ladakh",
        LD: "Lakshadweep",
        MP: "Madhya Pradesh",
        MH: "Maharashtra",
        MN: "Manipur",
        ML: "Meghalaya",
        MZ: "Mizoram",
        NL: "Nagaland",
        OR: "Odisha",
        PY: "Puducherry",
        PB: "Punjab",
        RJ: "Rajasthan",
        SK: "Sikkim",
        TN: "Tamil Nadu",
        TS: "Telangana",
        TR: "Tripura",
        UP: "Uttar Pradesh",
        UK: "Uttarakhand",
        WB: "West Bengal"
    };

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    // 🔹 Fetch existing address and parse it into components
    useEffect(() => {
        const fetchAddress = async () => {
            try {
                const response = await axios.get(
                    `${apiBaseUrl}/user/getAddress/${addressId}`
                );
                const address = response.data.data;

                // Parse the existing address into components
                const parsedAddress = parseAddress(address.address || '');

                setFormData({
                    name: address.name || '',
                    mobile: address.mobile || '',
                    pincode: address.pincode || '',
                    locality: address.locality || '',
                    houseNo: address.houseNo || '',
                    buildingName: address.buildingName || '',
                    street: address.street ||'',
                    landmark: address.landmark || '',
                    area: address.area || '',
                    city: address.city || '',
                    district: address.district || '',
                    state: address.state || '',
                    addressType: address.addressType || 'Home',
                    ledcode: userId,
                });
            } catch (error) {
                console.error('Error fetching address:', error);
                alert('Failed to fetch address details');
            }
        };

        fetchAddress();
    }, [addressId, apiBaseUrl, userId]);

    // 🔹 Parse existing address string into components
    const parseAddress = (addressString) => {
        const defaultParsed = {
            houseNo: '',
            buildingName: '',
            street: '',
            landmark: '',
            area: '',
        };

        if (!addressString) return defaultParsed;

        const parts = addressString.split(',');
        let houseNo = '';
        let remainingParts = [...parts];

        if (parts[0] && /\d+/.test(parts[0])) {
            houseNo = parts[0].trim();
            remainingParts = parts.slice(1);
        }

        return {
            houseNo: houseNo,
            buildingName: remainingParts[0]?.trim() || '',
            street: remainingParts[1]?.trim() || '',
            landmark: remainingParts[2]?.trim() || '',
            area: remainingParts[3]?.trim() || remainingParts[0]?.trim() || '',
        };
    };

    // 🔹 Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // 🔹 Build combined address from components
    const buildCombinedAddress = () => {
        const addressParts = [];

        if (formData.houseNo) addressParts.push(formData.houseNo);
        if (formData.buildingName) addressParts.push(formData.buildingName);
        if (formData.street) addressParts.push(formData.street);
        if (formData.landmark) addressParts.push(formData.landmark);
        if (formData.area) addressParts.push(formData.area);

        return addressParts.join(', ');
    };

    // 🔹 Fetch city & district from PIN
    const fetchPinDetails = async (pin) => {
        if (pin.length !== 6) return;

        try {
            const res = await axios.get(
                `${apiBaseUrl}/delhivery-pincode/${pin}`
            );
            const pinInfo = res.data?.data?.delivery_codes?.[0]?.postal_code;

            if (pinInfo) {
                const stateName = STATE_MAP[pinInfo.state_code];

                setFormData((prev) => ({
                    ...prev,
                    city: pinInfo.city || prev.city,
                    district: pinInfo.district || prev.district,
                    area: pinInfo.area || prev.area,
                    state: stateName || prev.state
                }));
            }
        } catch (err) {
            console.error('PIN fetch failed:', err);
        }
    };

    // 🔹 Validate address before submit
    const validateAddress = () => {
        const errors = [];

        if (!formData.name.trim()) errors.push('Name is required');

        if (!/^\d{10}$/.test(formData.mobile)) {
            errors.push('Enter valid 10-digit mobile number');
        }

        if (!/^\d{6}$/.test(formData.pincode)) {
            errors.push('Enter valid 6-digit pincode');
        }

        if (!formData.houseNo.trim()) {
            errors.push('House/Flat number is required');
        }

        if (!formData.area.trim() && !formData.locality.trim()) {
            errors.push('Area or Locality is required');
        }

        if (!formData.city || !formData.district) {
            errors.push('City and District are required');
        }

        if (errors.length > 0) {
            alert(errors.join('\n'));
            return false;
        }

        return true;
    };

    // 🔹 Submit handler
    const handleSubmit = async () => {
        if (!validateAddress()) return;

        const finalAddress = buildCombinedAddress();

        const payload = {
            name: formData.name,
            mobile: formData.mobile,
            pincode: formData.pincode,
            locality: formData.locality,
            houseNo: formData.houseNo,
            buildingName: formData.buildingName,
            street: formData.street,
            landmark: formData.landmark,
            area: formData.area,
            address: finalAddress,
            city: formData.city,
            district: formData.district,
            state: formData.state,
            addressType: formData.addressType,
            ledcode: formData.ledcode
        };

        try {
            const response = await axios.post(
                `${apiBaseUrl}/user/updateAddress/${addressId}`,
                payload
            );

            alert(response.data.message || 'Address updated successfully');
            fetchAddresses();
            onClose();
        } catch (error) {
            console.error('Error updating address:', error);
            alert('Failed to update address');
        }
    };


    return (
        <div className='fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50'>
            <div className='lg:w-1/2 w-full bg-white rounded-lg p-6 relative max-h-[90vh] overflow-y-auto'>
                <button
                    onClick={onClose}
                    className='absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 rounded-full w-8 h-8 flex items-center justify-center'
                >
                    &times;
                </button>

                <div className='flex flex-col gap-4'>
                    <h2 className='text-xl font-bold text-gray-800'>EDIT ADDRESS</h2>
                    <hr className='border-gray-300' />

                    <h3 className='font-semibold text-gray-700'>CONTACT DETAILS</h3>

                    <div className='space-y-1'>
                        <label className='block text-sm font-medium text-gray-600'>
                            Full Name *
                        </label>
                        <input
                            type="text"
                            name="name"
                            // placeholder="Enter your full name"
                            value={formData.name}
                            onChange={handleChange}
                            className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                            required
                        />
                    </div>

                    <div className='space-y-1'>
                        <label className='block text-sm font-medium text-gray-600'>
                            Mobile Number *
                        </label>
                        <input
                            type="text"
                            name="mobile"
                            // placeholder="10-digit mobile number"
                            value={formData.mobile}
                            onChange={handleChange}
                            className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                            maxLength="10"
                            required
                        />
                    </div>

                    <h3 className='font-semibold text-gray-700 mt-4'>ADDRESS DETAILS</h3>

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-1'>
                            <label className='block text-sm font-medium text-gray-600'>
                                Pincode *
                            </label>
                            <input
                                type="text"
                                name="pincode"
                                // placeholder="6-digit pincode"
                                value={formData.pincode}
                                onChange={(e) => {
                                    handleChange(e);
                                    fetchPinDetails(e.target.value);
                                }}
                                className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                maxLength="6"
                                required
                            />
                        </div>
                        <div className='space-y-1'>
                            <label className='block text-sm font-medium text-gray-600'>
                                Locality / Town
                            </label>
                            <input
                                type="text"
                                name="locality"
                                // placeholder="Enter locality/town"
                                value={formData.locality}
                                onChange={handleChange}
                                className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                            />
                        </div>
                    </div>

                    {/* 🔹 Separate Address Components with Labels */}
                    <div className='space-y-4 mt-2'>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-1'>
                                <label className='block text-sm font-medium text-gray-600'>
                                    House/Flat No. *
                                </label>
                                <input
                                    type="text"
                                    name="houseNo"
                                    // placeholder="e.g., 123, Flat 4A"
                                    value={formData.houseNo}
                                    onChange={handleChange}
                                    className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    required
                                />
                            </div>
                            <div className='space-y-1'>
                                <label className='block text-sm font-medium text-gray-600'>
                                    Building/Apartment Name
                                </label>
                                <input
                                    type="text"
                                    name="buildingName"
                                    // placeholder="e.g., Sunshine Apartments"
                                    value={formData.buildingName}
                                    onChange={handleChange}
                                    className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                            </div>
                        </div>



                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-1'>
                                <label className='block text-sm font-medium text-gray-600'>
                                    Street/Road Name
                                </label>
                                <input
                                    type="text"
                                    name="street"
                                    // placeholder="e.g., MG Road, Park Street"
                                    value={formData.street}
                                    onChange={handleChange}
                                    className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                            </div>
                            <div className='space-y-1'>
                                <label className='block text-sm font-medium text-gray-600'>
                                    Landmark (Optional)
                                </label>
                                <input
                                    type="text"
                                    name="landmark"
                                    // placeholder="e.g., Near City Mall"
                                    value={formData.landmark}
                                    onChange={handleChange}
                                    className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                            </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-1'>
                                <label className='block text-sm font-medium text-gray-600'>
                                    Area/Locality *
                                </label>
                                <input
                                    type="text"
                                    name="area"
                                    // placeholder="e.g., Koramangala, Bandra West"
                                    value={formData.area}
                                    onChange={handleChange}
                                    className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    required
                                />
                            </div>
                            <div className='space-y-1'>
                                <label className='block text-sm font-medium text-gray-600'>
                                    City *
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    // placeholder="e.g., Mumbai, Bangalore"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className='grid grid-cols-2 gap-4 mt-2'>
                        <div className='space-y-1'>
                            <label className='block text-sm font-medium text-gray-600'>
                                District *
                            </label>
                            <input
                                type="text"
                                name="district"
                                // placeholder="e.g., South Delhi, Bengaluru Urban"
                                value={formData.district}
                                onChange={handleChange}
                                className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                required
                            />
                        </div>
                        <div className='space-y-1'>
                            <label className='block text-sm font-medium text-gray-600'>
                                State *
                            </label>
                            <input
                                type="text"
                                name="state"
                                // placeholder="e.g., South Delhi, Bengaluru Urban"
                                value={formData.state}
                                onChange={handleChange}
                                className='w-full h-[40px] border border-gray-300 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                                required
                            />
                        </div>
                    </div>

                    {/* 🔹 Address Preview */}
                    {/* <div className='mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200'>
            <h4 className='font-semibold text-gray-700 mb-2'>Address Preview:</h4>
            <div className='p-3 bg-white rounded border border-gray-300 text-gray-600 min-h-[60px]'>
              {buildCombinedAddress() ? (
                <>
                  <p className='font-medium'>{buildCombinedAddress()}</p>
                  {formData.city && (
                    <p className='mt-1 text-sm'>
                      {formData.city}
                      {formData.district && `, ${formData.district}`}
                      {formData.pincode && ` - ${formData.pincode}`}
                    </p>
                  )}
                </>
              ) : (
                <p className='text-gray-400 italic'>Complete the form to see address preview...</p>
              )}
            </div>
            <p className='text-xs text-gray-500 mt-2'>
              * Required fields must be filled for successful delivery
            </p>
          </div> */}

                    <div className='mt-4'>
                        <h4 className='font-semibold text-gray-700 mb-3'>ADDRESS TYPE</h4>
                        <div className='flex gap-6'>
                            <label className='flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-300 hover:bg-gray-50 w-1/2'>
                                <input
                                    type="radio"
                                    name="addressType"
                                    value="Home"
                                    checked={formData.addressType === 'Home'}
                                    onChange={handleChange}
                                    className='w-5 h-5 text-blue-600'
                                />
                                <div>
                                    <span className='font-medium text-gray-700'>Home</span>
                                    <p className='text-xs text-gray-500'>Residential address</p>
                                </div>
                            </label>
                            <label className='flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-300 hover:bg-gray-50 w-1/2'>
                                <input
                                    type="radio"
                                    name="addressType"
                                    value="Office"
                                    checked={formData.addressType === 'Office'}
                                    onChange={handleChange}
                                    className='w-5 h-5 text-blue-600'
                                />
                                <div>
                                    <span className='font-medium text-gray-700'>Office</span>
                                    <p className='text-xs text-gray-500'>Workplace address</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className='flex gap-4 mt-8'>
                        <button
                            onClick={handleSubmit}
                            className='flex-1 h-[48px] bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Update Address
                        </button>
                        <button
                            onClick={onClose}
                            className='flex-1 h-[48px] border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors'
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditAddress;